/*
 Планировщик длинной работы порциями.

 Когда нужно пройти по очень большому массиву (например, сверка списка тегов с кешем),
 один синхронный проход превращается в длинную задачу и подвешивает главный
 поток. Здесь работа разбивается на короткие слайсы и выполняется в свободное
 время браузера: requestIdleCallback, а где его нет (Safari) — setTimeout.

 Шаг сам решает, сколько успеет сделать: он крутит свой цикл, пока hasTime()
 возвращает true, и возвращает true, когда работа закончена целиком.
*/

/** Шаг работы: выполняет столько, сколько разрешает hasTime, и возвращает true, когда всё сделано. */
export type IdleChunkStep = (hasTime: () => boolean) => boolean;

/** Сколько миллисекунд подряд разрешено занимать главный поток за один слайс. */
const DEFAULT_BUDGET_MS = 5;

/** Через сколько миллисекунд браузер обязан выполнить слайс, даже если простоя так и не случилось. */
const IDLE_TIMEOUT_MS = 200;

/**
 * Запускает шаг порциями. Возвращает функцию отмены: после её вызова
 * запланированный слайс не выполнится, а текущий прервётся на ближайшей
 * проверке hasTime().
 */
export function runIdleChunks(step: IdleChunkStep, budgetMs: number = DEFAULT_BUDGET_MS): () => void {
  const supportsIdle = typeof requestIdleCallback === "function";

  let cancelled = false;
  let idleHandle = 0;
  let timeoutHandle = 0;

  const slice = () => {
    idleHandle = 0;
    timeoutHandle = 0;
    if (cancelled) {
      return;
    }

    const start = performance.now();
    const hasTime = () => !cancelled && performance.now() - start < budgetMs;

    if (step(hasTime)) {
      return;
    }
    schedule();
  };

  const schedule = () => {
    if (cancelled) {
      return;
    }
    if (supportsIdle) {
      idleHandle = requestIdleCallback(() => slice(), { timeout: IDLE_TIMEOUT_MS });
    } else {
      timeoutHandle = window.setTimeout(() => slice(), 0);
    }
  };

  schedule();

  return () => {
    cancelled = true;
    if (idleHandle && typeof cancelIdleCallback === "function") {
      cancelIdleCallback(idleHandle);
    }
    if (timeoutHandle) {
      window.clearTimeout(timeoutHandle);
    }
  };
}
